/**
 * Login Component
 *
 * Provides the login form UI with email/password authentication.
 * Handles form validation, error display, and redirection after successful login.
 */

import { useState, useCallback, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth, type LoginCredentials, type AuthError } from '../../contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

interface FormState {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Validates email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates the login form
 */
function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.email.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!form.password) {
    errors.password = 'Password is required';
  }

  return errors;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Login page component
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, isAuthenticated, error: authError, clearError } = useAuth();

  const [form, setForm] = useState<FormState>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the redirect path from location state or default to dashboard
  const locationState = location.state as LocationState | null;
  const redirectPath = locationState?.from?.pathname ?? '/dashboard';

  /**
   * Handle form field changes
   */
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));

      // Clear field-specific error when user starts typing
      if (formErrors[name as keyof FormErrors]) {
        setFormErrors((prev) => ({ ...prev, [name]: undefined }));
      }

      // Clear auth error when user modifies form
      if (authError) {
        clearError();
      }
    },
    [formErrors, authError, clearError]
  );

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Validate form
      const errors = validateForm(form);
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      setIsSubmitting(true);
      setFormErrors({});

      try {
        const credentials: LoginCredentials = {
          email: form.email.trim().toLowerCase(),
          password: form.password,
        };

        const result = await login(credentials);

        if (result.success) {
          // Navigate to the intended destination
          navigate(redirectPath, { replace: true });
        }
        // If login failed, the error will be in authError via context
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, login, navigate, redirectPath]
  );

  /**
   * Get user-friendly error message from auth error
   */
  const getErrorMessage = (error: AuthError): string => {
    switch (error.code) {
      case 'INVALID_CREDENTIALS':
        return 'Invalid email or password. Please try again.';
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your connection.';
      case 'USER_INACTIVE':
        return 'This account has been deactivated. Please contact support.';
      case 'VALIDATION_ERROR':
        return error.message;
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  };

  const isFormDisabled = isLoading || isSubmitting;

  // If already authenticated, redirect to the target page
  // Note: This must be after all hooks to comply with React's rules of hooks
  if (isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-900 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white">
            <span className="text-discord-blurple">Discord</span>Minstrel
          </h1>
          <p className="mt-2 text-gray-400">
            Multi-Tenant AI Bot Management Platform
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-lg bg-gray-800 p-8 shadow-xl">
          <h2 className="mb-6 text-xl font-semibold text-white">Sign In</h2>

          {/* Error Alert */}
          {authError && (
            <div className="mb-6 rounded-md bg-red-900/50 border border-red-500 p-4">
              <div className="flex items-start">
                <svg
                  className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm text-red-200">
                    {getErrorMessage(authError)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            {/* Email Field */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="mb-2 block text-sm font-medium text-gray-300"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                disabled={isFormDisabled}
                autoComplete="email"
                autoFocus
                className={`
                  w-full rounded-md border bg-gray-700 px-4 py-3 text-white
                  placeholder-gray-400 transition-colors
                  focus:outline-none focus:ring-2 focus:ring-discord-blurple
                  disabled:cursor-not-allowed disabled:opacity-50
                  ${formErrors.email ? 'border-red-500' : 'border-gray-600'}
                `}
                placeholder="you@example.com"
                aria-invalid={!!formErrors.email}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
              {formErrors.email && (
                <p
                  id="email-error"
                  className="mt-1 text-sm text-red-400"
                  role="alert"
                >
                  {formErrors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-6">
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-300"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                disabled={isFormDisabled}
                autoComplete="current-password"
                className={`
                  w-full rounded-md border bg-gray-700 px-4 py-3 text-white
                  placeholder-gray-400 transition-colors
                  focus:outline-none focus:ring-2 focus:ring-discord-blurple
                  disabled:cursor-not-allowed disabled:opacity-50
                  ${formErrors.password ? 'border-red-500' : 'border-gray-600'}
                `}
                placeholder="Enter your password"
                aria-invalid={!!formErrors.password}
                aria-describedby={
                  formErrors.password ? 'password-error' : undefined
                }
              />
              {formErrors.password && (
                <p
                  id="password-error"
                  className="mt-1 text-sm text-red-400"
                  role="alert"
                >
                  {formErrors.password}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isFormDisabled}
              className={`
                w-full rounded-md bg-discord-blurple px-4 py-3 font-semibold
                text-white transition-colors
                hover:bg-discord-blurple/90
                focus:outline-none focus:ring-2 focus:ring-discord-blurple focus:ring-offset-2
                focus:ring-offset-gray-800
                disabled:cursor-not-allowed disabled:opacity-50
              `}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="h-5 w-5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500">
          Need help?{' '}
          <a
            href="mailto:support@example.com"
            className="text-discord-blurple hover:underline"
          >
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;
